import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import VerifyEmail from '@/pages/VerifyEmail';
import VerifyEmailConfirm from '@/pages/VerifyEmailConfirm';
import MailingUnsubscribe from '@/pages/MailingUnsubscribe';
import Dashboard from '@/pages/Dashboard';
import Cartola from '@/pages/Cartola';
import MelCartolaPanel from '@/components/mel/MelCartolaPanel';
import CollectionScheduleAllPanel from '@/components/adelantos/CollectionScheduleAllPanel';
import Movimientos from '@/pages/Movimientos';

import AdelantosIndex from '@/pages/adelantos/Index';
import AdelantosCuotas from '@/pages/adelantos/Cuotas';
import AdelantosDetalle from '@/pages/adelantos/Detalle';
import AdelantosSolicitudes from '@/pages/adelantos/Solicitudes';

import ConfiguracionesLayout from '@/pages/configuraciones/Layout';
import ConfiguracionesIndex from '@/pages/configuraciones/Index';
import ConexionSii from '@/pages/configuraciones/ConexionSii';
import CredencialesAdquirente from '@/pages/configuraciones/CredencialesAdquirente';
import CuentaBancaria from '@/pages/configuraciones/CuentaBancaria';
import PagoAutomaticoCuentas from '@/pages/configuraciones/PagoAutomaticoCuentas';
import FirmaPagare from '@/pages/configuraciones/FirmaPagare';
import PagoAutomaticoTarjetas from '@/pages/configuraciones/PagoAutomaticoTarjetas';
import Resumen from '@/pages/configuraciones/Resumen';
import Terminos from '@/pages/configuraciones/Terminos';

import ProtectedRoute from '@/components/auth/ProtectedRoute';

import AdminLayout from '@/pages/admin/Layout';
import AdminIndex from '@/pages/admin/Index';
import AdminLogin from '@/pages/admin/Login';
import AdminDashboard from '@/pages/admin/Dashboard';
import AdminEmpresasIndex from '@/pages/admin/empresas/Index';
import AdminEmpresasDetalle from '@/pages/admin/empresas/Detalle';
import AdminSolicitudesIndex from '@/pages/admin/solicitudes/Index';
import AdminSolicitudesDetalle from '@/pages/admin/solicitudes/Detalle';
import AdminAlertasIndex from '@/pages/admin/alertas/Index';
import AdminAlertasAccountChanges from '@/pages/admin/alertas/AccountChanges';
import AdminAbandonedInstallments from '@/pages/admin/AbandonedInstallments';
import AdminPnolDispatches from '@/pages/admin/PnolDispatches';
import AdminPnolDispatchDetalle from '@/pages/admin/PnolDispatchDetalle';
import AdminScrapperEvents from '@/pages/admin/ScrapperEvents';
import AdminLupsEvents from '@/pages/admin/LupsEvents';
import AdminPaymentRequests from '@/pages/admin/PaymentRequests';
import AdminCalculadora from '@/pages/admin/Calculadora';
import AdminOtps from '@/pages/admin/Otps';
import AdminTokens from '@/pages/admin/Tokens';
import AdminPacComprobantes from '@/pages/admin/pac/Comprobantes';
import AdminPacUniverso from '@/pages/admin/pac/Universo';
import AdminPacBatches from '@/pages/admin/pac/Batches';
import AdminPacFallidos from '@/pages/admin/pac/Fallidos';
// ⚠️ Ruta oculta — solo para pruebas. NO listar en menús/sidebar.
// El backend bloquea la ejecución cuando NODE_ENV=production.
import AdminSeedDemo from '@/pages/admin/SeedDemo';

function RootRedirect() {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  return <Navigate to={user ? '/dashboard' : '/login'} replace />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />

      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/verify-email/confirm" element={<VerifyEmailConfirm />} />
      <Route path="/mailing-unsubscribe" element={<MailingUnsubscribe />} />

      {/* Subárbol comercio: ProtectedRoute aplica el guard una sola vez en
          App.tsx en lugar de depender de que cada página recuerde envolverse
          en AppLayout. */}
      <Route element={<ProtectedRoute />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/cartola" element={<Cartola />}>
          <Route index element={<Navigate to="linea" replace />} />
          <Route path="linea" element={<MelCartolaPanel />} />
          <Route path="cobranza" element={<CollectionScheduleAllPanel />} />
        </Route>
        <Route path="/movimientos" element={<Movimientos />} />

        <Route path="/adelantos" element={<AdelantosIndex />} />
        <Route path="/adelantos/cuotas" element={<AdelantosCuotas />} />
        <Route path="/adelantos/detalle" element={<AdelantosDetalle />} />
        <Route path="/adelantos/solicitudes" element={<AdelantosSolicitudes />} />

        <Route path="/configuraciones" element={<ConfiguracionesLayout />}>
          <Route index element={<ConfiguracionesIndex />} />
          <Route path="conexion-sii" element={<ConexionSii />} />
          <Route path="credenciales-adquirente" element={<CredencialesAdquirente />} />
          <Route path="cuenta-bancaria" element={<CuentaBancaria />} />
          <Route path="pago-automatico-cuentas" element={<PagoAutomaticoCuentas />} />
          <Route path="firma-pagare" element={<FirmaPagare />} />
          <Route path="pago-automatico-tarjetas" element={<PagoAutomaticoTarjetas />} />
          <Route path="resumen" element={<Resumen />} />
          <Route path="terminos" element={<Terminos />} />
        </Route>
      </Route>

      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminIndex />} />
        <Route path="login" element={<AdminLogin />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="empresas" element={<AdminEmpresasIndex />} />
        <Route path="empresas/detalle" element={<AdminEmpresasDetalle />} />
        <Route path="solicitudes" element={<AdminSolicitudesIndex />} />
        <Route path="solicitudes/detalle" element={<AdminSolicitudesDetalle />} />
        <Route path="alertas" element={<AdminAlertasIndex />} />
        <Route path="alertas/account-changes" element={<AdminAlertasAccountChanges />} />
        <Route path="abandoned-installments" element={<AdminAbandonedInstallments />} />
        <Route path="pnol-dispatches" element={<AdminPnolDispatches />} />
        <Route path="pnol-dispatches/:id" element={<AdminPnolDispatchDetalle />} />
        <Route path="scrapper-events" element={<AdminScrapperEvents />} />
        <Route path="lups-webhooks" element={<AdminLupsEvents />} />
        <Route path="cobros" element={<AdminPaymentRequests />} />
        <Route path="calculadora" element={<AdminCalculadora />} />
        <Route path="otps" element={<AdminOtps />} />
        <Route path="tokens" element={<AdminTokens />} />
        <Route path="pac/comprobantes" element={<AdminPacComprobantes />} />
        <Route path="pac/universo" element={<AdminPacUniverso />} />
        <Route path="pac/batches" element={<AdminPacBatches />} />
        <Route path="pac/fallidos" element={<AdminPacFallidos />} />
        {/* Ruta oculta — solo para pruebas, no aparece en sidebar/menus.
            El servicio backend valida NODE_ENV !== 'production' antes de ejecutar. */}
        <Route path="seed-demo" element={<AdminSeedDemo />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
